/**
 * Task-API error vocabulary and request plumbing — a LEAF module.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS ITS OWN FILE (ADR-020 story 3.4)
 * ---------------------------------------------------------------------------
 * `@/lib/api/tasks` is mocked WHOLESALE by eleven suites, only three of which
 * use `importOriginal`. Story 3.3 already paid for that once: the moment
 * `useChatUrlState` read a value from it at module load, two shell suites went
 * from green to failing to IMPORT, and the answer was to move the vocabulary
 * into a leaf (`task-kinds.ts`) that nobody has a reason to mock.
 *
 * Story 3.4 hits the same wall one layer down. The artifact picker lives inside
 * `TaskThreadPanel`, which `UnifiedShell.test.tsx` and
 * `MobileResponsiveShell.test.tsx` both MOUNT — so a picker that imported its
 * filing calls from `@/lib/api/tasks` would break those two suites at import,
 * not at assertion, exactly as before. The calls therefore live in
 * `@/lib/api/task-links`, and that module needs this plumbing.
 *
 * It could not simply import it from `tasks.ts`: that would make `task-links`
 * depend on the wholesale-mocked module and re-arm the very landmine it exists
 * to disarm. Nor could it own a second copy of `TaskApiError` — two error
 * classes means `instanceof` starts lying somewhere. So the plumbing moved
 * HERE, `tasks.ts` re-exports every symbol it used to own (so all existing
 * import sites are untouched), and both API modules import from this leaf.
 *
 * Nothing here does I/O of its own beyond shaping an axios failure, and there
 * is exactly ONE definition of each symbol.
 */

import axios from 'axios';
import { getAccessToken } from '@/lib/auth/accessToken';

/**
 * Harness origin — an alias of the single `HARNESS_BASE_URL` (T-20260927-6),
 * kept under its old name so `tasks.ts` and its importers are untouched.
 */
export { HARNESS_BASE_URL as harnessBase } from './client';

export function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * TRAP: The casing is deliberately NOT uniform, because the server's is not.
 * Route-level 503s answer with lowercase snake (`tasks_history_failed`,
 * `thread_post_failed` — `routes/tasks.ts:325`, `task-thread.ts:492`, `:1690`)
 * while every validation / authz / not-found code is SCREAMING_SNAKE. This
 * union mirrors what the wire actually sends; normalising it here would mean
 * `throwFromAxiosError` produced a code that matches no server string, and the
 * `code`-branching consumers would silently stop matching. Do not "fix" it.
 */
export type TaskErrorCode =
  | 'TASK_INVALID_INPUT'
  | 'TASK_FEEDBACK_REQUIRED'
  | 'TASK_INVALID_TRANSITION'
  | 'TASK_INVALID_FILTER'
  | 'TASK_INVALID_CURSOR'
  | 'TASK_AUTHZ_FORBIDDEN'
  | 'TASK_NOT_FOUND'
  // ADR-020 story 3.1 REVIEW — the two codes the shared `requirePermission` /
  // `requireTaskActionPermission` gates emit. Story 3.1 mounted those on all
  // five task routes, so both are now reachable on this surface: `FORBIDDEN`
  // for a viewer or an unknown role, `UNAUTHORIZED` for a missing role claim.
  // They are NOT spellings of `TASK_AUTHZ_FORBIDDEN` (which `applyTaskAction`'s
  // in-transaction backstop emits) and must not be normalised into it — per
  // this union's own rule, it mirrors what the wire actually sends.
  // `useTaskThreadStream` already branches on `'FORBIDDEN'` for the thread
  // lane; without these two, every lifecycle consumer's `code` comparison fell
  // through to the generic error path.
  | 'FORBIDDEN'
  | 'UNAUTHORIZED'
  // ADR-020 story 2.2 — the ticket thread lane.
  | 'TASK_THREAD_READ_ONLY'
  // ADR-020 story 2.6 — the dispatch guardrails. Both are SYNCHRONOUS refusals
  // that land NO durable post: they fire before the human's question is
  // persisted, so there is nothing on the ticket to explain and the composer is
  // the whole surface. (Contrast `INSUFFICIENT_CREDIT`, which DOES carry a
  // `post.id` because its question was already durable.)
  | 'TASK_THREAD_DISPATCH_IN_FLIGHT'
  | 'TASK_THREAD_RATE_LIMITED'
  | 'INSUFFICIENT_CREDIT'
  | 'tasks_create_failed'
  | 'tasks_patch_failed'
  // T-20260919-13 — the stack's bulk Cancel (POST /api/tasks/bulk), when no
  // envelope code arrived. Its own code: a failed batch is not a failed PATCH.
  | 'tasks_bulk_failed'
  | 'tasks_detail_failed'
  | 'tasks_history_failed'
  // T-20260906-4 — the on-demand session-summary read. Its own 503 code rather
  // than borrowing `tasks_history_failed`, which would mislead whoever greps
  // logs for a failed thread read: this route is a sibling, not the history.
  | 'tasks_session_summary_failed'
  // T-20260913-7 — the "Steps taken" sibling read, same reasoning.
  | 'tasks_session_steps_failed'
  // F-20261012-8 — the "View full proposed skill" sibling read, same reasoning.
  | 'tasks_proposed_skill_failed'
  // Story 38.3 — the media-promotion preview sibling read (the server's 503
  // code, and the client fallback when no envelope code arrived).
  | 'tasks_media_preview_unavailable'
  // Story 39.2 — the skill-link card sibling read, same reasoning.
  | 'tasks_skill_link_unavailable'
  // Story 26.6 — the record-link read behind the thread's record chip.
  | 'tasks_record_link_failed'
  // ADR-020 story 3.2 REVIEW — the task-type registry listing gets its own
  // codes rather than borrowing `tasks_detail_failed`, which would mislead
  // whoever greps logs for detail-fetch failures. `TASK_TYPES_UNAVAILABLE` is
  // the server's 503 (registry read failed — retry); `task_types_list_failed`
  // is the client-side fallback when no envelope code arrived.
  | 'TASK_TYPES_UNAVAILABLE'
  | 'task_types_list_failed'
  // ADR-020 story 3.4 — the filing lane. `TASK_ARTIFACT_ALREADY_FILED` is the
  // 409 the picker turns into "already on this ticket" rather than a generic
  // failure; `TASK_ARTIFACT_NOT_FOUND` is the 404 for a target that does not
  // resolve AND for a link that is already unfiled — the same non-disclosure
  // code for both, deliberately. `task_file_failed` / `task_unfile_failed` are
  // the client-side fallbacks when no envelope code arrived.
  | 'TASK_ARTIFACT_ALREADY_FILED'
  | 'TASK_ARTIFACT_NOT_FOUND'
  | 'TASK_FILE_FAILED'
  | 'TASK_UNFILE_FAILED'
  | 'task_file_failed'
  | 'task_unfile_failed'
  // T-20260914-11d — the review-queue pager's list read. `routes/tasks.ts`
  // answers a failed GET /api/tasks query with this 503 code, and has since
  // before the pager existed; the pager is the first SPA consumer to branch
  // on it, so the union now mirrors the wire per its own rule.
  | 'tasks_query_failed'
  // T-20260917-21a/b — POST /api/tasks/:id/continue-in-chat. The two 422s are
  // the route's originator refusals (no agent raised the ticket / that agent is
  // gone); the lowercase code is its 503 and the client-side fallback.
  | 'TASK_NO_AGENT_ORIGINATOR'
  | 'TASK_ORIGINATOR_UNAVAILABLE'
  | 'tasks_continue_in_chat_failed'
  | 'thread_post_failed';

/**
 * F-20261012-7 — the closed structured companion an executor refusal may carry
 * on its 422 (`error.details`). Mirrors `ExecutorErrorDetails` in the harness's
 * `task-executors.ts`. Parsed defensively by `parseTaskErrorDetails`: anything
 * that is not exactly a known shape is dropped to `null`, never passed through.
 */
export interface TaskErrorCandidateCustomer {
  refId: string;
  first_name: string;
  last_name: string;
  email: string;
}

export type TaskErrorDetails = {
  code: 'CUSTOMER_EMAIL_EXISTS';
  candidate: TaskErrorCandidateCustomer;
};

const DETAILS_MAX_REF_ID = 64;
const DETAILS_MAX_NAME = 100;
const DETAILS_MAX_EMAIL = 200;

export function parseTaskErrorDetails(raw: unknown): TaskErrorDetails | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { code, candidate } = raw as { code?: unknown; candidate?: unknown };
  if (code !== 'CUSTOMER_EMAIL_EXISTS') return null;
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  const c = candidate as Record<string, unknown>;
  // Bounded like the card's own candidate schema (`RecipeSheetCandidateSchema`):
  // an over-long value is refused whole, never truncated — a truncated email
  // would no longer match the one the server binds the resubmit to.
  const bounded = (v: unknown, min: number, max: number): v is string =>
    typeof v === 'string' && v.length >= min && v.length <= max;
  if (!bounded(c.refId, 1, DETAILS_MAX_REF_ID)) return null;
  if (!bounded(c.first_name, 0, DETAILS_MAX_NAME)) return null;
  if (!bounded(c.last_name, 0, DETAILS_MAX_NAME)) return null;
  if (!bounded(c.email, 0, DETAILS_MAX_EMAIL)) return null;
  return {
    code,
    candidate: { refId: c.refId, first_name: c.first_name, last_name: c.last_name, email: c.email },
  };
}

export class TaskApiError extends Error {
  readonly code: TaskErrorCode;
  readonly status: number;
  /**
   * The durable post the server ALREADY landed before failing, when it says so.
   *
   * A `402 INSUFFICIENT_CREDIT` and the outer `503` both answer
   * `{ error: {...}, post: { id } }` — the human's question survived, and the
   * failure notice is a second row, not a replacement for it
   * (`task-thread.ts:722-742`, `:1687-1696`). Carrying the id here is what lets
   * the client SWAP its optimistic row for the real one instead of deleting the
   * row and waiting for a refetch to bring it back, which is the flicker AC2's
   * "a `402` carries a post id in its body. Use it." exists to prevent.
   *
   * `null` whenever the envelope carries no post — most failures land nothing.
   */
  readonly postId: string | null;
  /**
   * F-20261012-7 — the 422's structured `error.details`, when the server sent a
   * known shape; `null` otherwise (every failure but an executor refusal that
   * names something the client can act on).
   */
  readonly details: TaskErrorDetails | null;
  constructor(
    code: TaskErrorCode,
    status: number,
    message?: string,
    postId: string | null = null,
    details: TaskErrorDetails | null = null,
  ) {
    super(message ?? code);
    this.name = 'TaskApiError';
    this.code = code;
    this.status = status;
    this.postId = postId;
    this.details = details;
  }
}

interface ErrorEnvelope {
  error?:
    | string
    | {
        code?: string;
        message?: string;
        details?: unknown;
      };
  /** Present on the failures that still landed a durable post. */
  post?: { id?: string };
}

export function throwFromAxiosError(
  err: unknown,
  fallback: TaskErrorCode,
): never {
  if (axios.isAxiosError(err) && err.response) {
    const envelope = err.response.data as ErrorEnvelope | undefined;
    const codeFromEnvelope =
      typeof envelope?.error === 'object' && envelope.error
        ? envelope.error.code
        : typeof envelope?.error === 'string'
          ? envelope.error
          : undefined;
    const code = (codeFromEnvelope as TaskErrorCode | undefined) ?? fallback;
    const message =
      typeof envelope?.error === 'object'
        ? envelope.error?.message
        : undefined;
    const postId =
      typeof envelope?.post?.id === 'string' && envelope.post.id.length > 0
        ? envelope.post.id
        : null;
    const details =
      typeof envelope?.error === 'object' && envelope.error
        ? parseTaskErrorDetails(envelope.error.details)
        : null;
    throw new TaskApiError(code, err.response.status, message, postId, details);
  }
  throw new TaskApiError(fallback, 0, err instanceof Error ? err.message : undefined);
}

